import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTrigramFromSums, getHexagram } from '@/mocks/hexagrams';
import { saveRecord } from '@/utils/divinationStorage';
import { consumeUsage, getUsageInfo } from '@/utils/usageStorage';
import PaywallModal from '@/components/feature/PaywallModal';

type CoinResult = '正面' | '反面' | null;

interface RoundRecord {
  round: number;
  results: CoinResult[];
  coinValues: number[];
  total: number;
}

export default function Divination() {
  const location = useLocation();
  const navigate = useNavigate();
  const question = (location.state as { question?: string })?.question || '';

  const [isSpinning, setIsSpinning] = useState(false);
  const [currentCoins, setCurrentCoins] = useState<CoinResult[]>([null, null, null]);
  const [rounds, setRounds] = useState<RoundRecord[]>([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [phase, setPhase] = useState<'ready' | 'spinning' | 'stopped' | 'complete'>('ready');
  const [showPaywall, setShowPaywall] = useState(false);
  const [usageInfo, setUsageInfo] = useState(getUsageInfo());
  const spinTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const COIN_VALUES: Record<string, number> = { '正面': 3, '反面': 2 };

  const tossCoins = useCallback((): CoinResult[] => {
    return Array.from({ length: 3 }, () => (Math.random() > 0.5 ? '正面' : '反面'));
  }, []);

  const startSpinning = useCallback(() => {
    setCurrentCoins([null, null, null]);
    setIsSpinning(true);
    setPhase('spinning');
    spinTimerRef.current = setInterval(() => {
      setCurrentCoins(tossCoins());
    }, 100);
  }, [tossCoins]);

  const handleNextRound = useCallback(() => {
    if (currentRound >= 6) return;
    startSpinning();
  }, [currentRound, startSpinning]);

  const handleStart = useCallback(() => {
    if (currentRound >= 6) return;

    // 第一次问爻时扣除次数
    if (currentRound === 0) {
      const ok = consumeUsage();
      if (!ok) {
        setShowPaywall(true);
        return;
      }
      setUsageInfo(getUsageInfo());
    }

    startSpinning();
  }, [currentRound, startSpinning]);

  const handleStop = useCallback(() => {
    if (!isSpinning) return;

    if (spinTimerRef.current) {
      clearInterval(spinTimerRef.current);
      spinTimerRef.current = null;
    }

    const finalCoins = tossCoins();
    setCurrentCoins(finalCoins);
    setIsSpinning(false);
    setPhase('stopped');

    const coinValues = finalCoins.map((c) => COIN_VALUES[c ?? '反面']);
    const total = coinValues.reduce((a, b) => a + b, 0);

    const newRound = currentRound + 1;
    const record: RoundRecord = {
      round: newRound,
      results: finalCoins,
      coinValues,
      total,
    };

    const newRounds = [...rounds, record];
    setRounds(newRounds);
    setCurrentRound(newRound);

    if (newRound >= 6) {
      setPhase('complete');
    }
  }, [isSpinning, currentRound, rounds, tossCoins, COIN_VALUES]);

  const handleViewResult = useCallback(() => {
    if (rounds.length !== 6) return;
    const allSums = rounds.map((r) => r.total);
    const lowerSums = allSums.slice(0, 3);
    const upperSums = allSums.slice(3, 6);
    const lowerTrigram = getTrigramFromSums(lowerSums);
    const upperTrigram = getTrigramFromSums(upperSums);
    const hexagram = getHexagram(upperTrigram, lowerTrigram);

    // 保存占卜记录
    const saved = saveRecord({
      question,
      hexagramName: hexagram.name,
      hexagramNumber: hexagram.number,
      upperTrigram,
      lowerTrigram,
      interpretation: '',
      rounds: rounds.map((r) => ({
        round: r.round,
        total: r.total,
        results: r.results as string[],
      })),
    });

    navigate('/interpretation', {
      state: {
        question,
        rounds,
        lowerTrigram,
        upperTrigram,
        hexagram,
        recordId: saved.id,
      },
    });
  }, [rounds, question, navigate]);

  useEffect(() => {
    return () => {
      if (spinTimerRef.current) {
        clearInterval(spinTimerRef.current);
      }
    };
  }, []);

  // 判断是否有变爻（和数为6或9）
  const getLineType = (total: number) => {
    if (total === 9) return { type: '阳', label: '老阳', changing: true, color: 'text-accent-700' };
    if (total === 7) return { type: '阳', label: '少阳', changing: false, color: 'text-accent-600' };
    if (total === 8) return { type: '阴', label: '少阴', changing: false, color: 'text-secondary-600' };
    if (total === 6) return { type: '阴', label: '老阴', changing: true, color: 'text-primary-600' };
    return { type: total % 2 === 1 ? '阳' : '阴', label: total % 2 === 1 ? '阳' : '阴', changing: false, color: 'text-foreground-600' };
  };

  const hasChangingLines = rounds.some(r => r.total === 6 || r.total === 9);

  return (
    <div className="flex flex-col items-center min-h-screen pt-8 pb-6">
      {/* 剩余次数提示 */}
      {usageInfo.remaining > 0 && usageInfo.remaining <= 5 && (
        <div className="w-full text-center mb-3 animate-fade-in">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200">
            <i className="ri-error-warning-line text-accent-600 text-xs"></i>
            <span className="text-xs text-accent-700 font-medium">
              剩余 {usageInfo.remaining} 次
            </span>
          </div>
        </div>
      )}

      <header className="w-full text-center mb-4 animate-fade-in">
        <p className="text-xs text-foreground-400 mb-1">
          第 {Math.min(currentRound + (phase === 'stopped' ? 0 : 1), 6)} / 6 爻
        </p>
        <div className="flex items-center justify-center gap-1 mb-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`w-6 h-1.5 rounded-full transition-colors duration-300 ${
                i < currentRound
                  ? 'bg-primary-500'
                  : i === currentRound && phase === 'spinning'
                    ? 'bg-accent-400 animate-pulse'
                    : 'bg-background-200'
              }`}
            ></div>
          ))}
        </div>
      </header>

      {question && (
        <div className="w-full bg-background-100 rounded-lg px-4 py-3 mb-5 text-center animate-fade-in border border-background-200">
          <p className="text-xs text-foreground-400 mb-1">所问之事</p>
          <p className="text-sm text-foreground-700 font-medium">{question}</p>
        </div>
      )}

      {/* 铜钱 */}
      <div className="flex items-center justify-center gap-6 my-6">
        {currentCoins.map((coin, index) => (
          <div
            key={index}
            className={`w-24 h-24 rounded-full flex items-center justify-center border-4 transition-all duration-300 ${
              isSpinning
                ? 'border-accent-400 animate-coin-flip bg-accent-100'
                : coin === '正面'
                  ? 'border-accent-500 bg-accent-50'
                  : coin === '反面'
                    ? 'border-secondary-400 bg-secondary-50'
                    : 'border-background-300 bg-background-100'
            }`}
          >
            {coin === '正面' && !isSpinning ? (
              <div className="text-center">
                <span className="font-heading text-2xl font-bold text-accent-600">正</span>
                <p className="text-xs text-accent-500">3</p>
              </div>
            ) : coin === '反面' && !isSpinning ? (
              <div className="text-center">
                <span className="font-heading text-2xl font-bold text-secondary-500">反</span>
                <p className="text-xs text-secondary-400">2</p>
              </div>
            ) : (
              <span className="font-heading text-2xl font-bold text-foreground-300">?</span>
            )}
          </div>
        ))}
      </div>

      {/* 当前爻结果提示 */}
      {phase === 'stopped' && rounds.length > 0 && (
        <div className="w-full mb-4 animate-fade-in">
          {(() => {
            const lastRound = rounds[rounds.length - 1];
            const lineInfo = getLineType(lastRound.total);
            return (
              <div className="bg-background-100 rounded-xl border border-background-200 px-4 py-3 text-center">
                <p className="text-xs text-foreground-400 mb-1">第 {lastRound.round} 爻</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-sm text-foreground-600">
                    和数：<strong className="text-foreground-900">{lastRound.total}</strong>
                  </span>
                  <span className={`text-sm font-bold ${lineInfo.color}`}>{lineInfo.label}</span>
                  {lineInfo.changing && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">变爻</span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* 操作按钮 */}
      <div className="w-full max-w-xs mb-6">
        {phase === 'stopped' && currentRound < 6 && (
          <div className="space-y-3">
            <div className="text-center text-sm text-foreground-600 font-medium py-2 bg-background-100 rounded-lg border border-background-200">
              第 {currentRound} 爻已定，还有{' '}
              <strong className="text-primary-600">{6 - currentRound}</strong> 爻
            </div>
            <button
              onClick={handleNextRound}
              className="w-full py-3.5 rounded-xl bg-accent-500 text-background-50 font-heading text-lg font-bold hover:bg-accent-600 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.98]"
            >
              问下一爻
            </button>
          </div>
        )}
        {phase !== 'stopped' && phase !== 'complete' && (
          <>
            {!isSpinning && (
              <button
                onClick={handleStart}
                className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 font-heading text-lg font-bold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.98]"
              >
                {currentRound === 0 ? '问爻' : '继续问爻'}
              </button>
            )}
            {isSpinning && (
              <button
                onClick={handleStop}
                className="w-full py-3.5 rounded-xl bg-accent-500 text-background-50 font-heading text-lg font-bold hover:bg-accent-600 transition-all duration-200 cursor-pointer whitespace-nowrap animate-pulse-glow active:scale-[0.98]"
              >
                停止
              </button>
            )}
          </>
        )}
      </div>

      {/* 摇卦记录 */}
      {currentRound > 0 && (
        <div className="w-full animate-fade-in">
          <h3 className="font-heading text-sm font-semibold text-foreground-700 mb-3 text-center">六爻记录</h3>
          <div className="space-y-2">
            {rounds.map((round) => {
              const lineInfo = getLineType(round.total);
              return (
                <div
                  key={round.round}
                  className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-2.5 border border-background-200"
                >
                  <span className="text-xs text-foreground-500 font-medium w-10">第{round.round}爻</span>
                  <div className="flex items-center gap-2 flex-1 justify-center">
                    {round.results.map((r, i) => (
                      <span
                        key={i}
                        className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                          r === '正面' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                        }`}
                      >
                        {r === '正面' ? '正' : '反'}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${lineInfo.color}`}>{lineInfo.label}</span>
                    <span className="text-sm font-heading font-bold text-primary-600 w-6 text-right">{round.total}</span>
                    {lineInfo.changing && (
                      <span className="text-xs text-primary-500">变</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 完成后显示 */}
      {phase === 'complete' && (
        <div className="w-full mt-5 animate-slide-up">
          <div className="bg-background-100 rounded-xl border border-background-200 p-4 mb-4">
            <p className="text-center text-sm text-foreground-700 mb-2 font-medium">六爻已定，卦象生成</p>
            <div className="flex justify-center gap-6 text-xs text-foreground-500 mb-2">
              <span>下卦: <strong className="text-foreground-800">{getTrigramFromSums(rounds.slice(0, 3).map((r) => r.total))}</strong></span>
              <span>上卦: <strong className="text-foreground-800">{getTrigramFromSums(rounds.slice(3, 6).map((r) => r.total))}</strong></span>
            </div>
            {hasChangingLines && (
              <p className="text-center text-xs text-primary-600 font-medium">发现变爻，将生成之卦与变卦一并解读</p>
            )}
          </div>
          <button
            onClick={handleViewResult}
            className="w-full py-4 rounded-xl bg-primary-500 text-background-50 font-heading text-lg font-bold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap active:scale-[0.98]"
          >
            查看卦象解读
          </button>
        </div>
      )}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
      />
    </div>
  );
}